import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  FlaskConical, Plus, Save, Trash, FileDown, ListChecks, BarChart3, Target, BookText, Wand2,
} from "lucide-react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

import FactorEditor from "../components/rnd/FactorEditor";
import TrialsTable from "../components/rnd/TrialsTable";
import AnalysisPanel from "../components/rnd/AnalysisPanel";
import OptimizePanel from "../components/rnd/OptimizePanel";

import { api } from "../lib/kostra";
import { useAuth } from "../lib/auth";

const EMPTY = {
  id: null,
  name: "Untitled R&D Project",
  category: "Sauce",
  objective: "multi_objective",
  notes: "",
  base_recipe_id: null,
  base_recipe_snapshot: null,
  factors: [],
  design_type: "ccd",
  design_meta: { center_points: 3, alpha: "rotatable" },
  trials: [],
  response_weights: {},
};

const CATEGORIES = ["Sauce", "Marinade", "Bakery item", "Beverage", "Dessert", "Snack", "Main course", "Other"];
const OBJECTIVES = [
  { value: "cost_reduction", label: "Cost Reduction" },
  { value: "taste", label: "Taste Optimization" },
  { value: "texture", label: "Texture Improvement" },
  { value: "yield", label: "Yield Maximization" },
  { value: "multi_objective", label: "Multi-objective" },
];

export default function RnDPage() {
  const { isAdmin, openLogin } = useAuth();
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(EMPTY);
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [chefRecipes, setChefRecipes] = useState([]);
  const [tab, setTab] = useState("setup");
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Bootstrap project list, recipes
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const [pr, sr, cr] = await Promise.all([
          api.get("/rnd/projects"),
          api.get("/recipes"),
          api.get("/chef-recipes"),
        ]);
        setProjects(pr.data || []);
        setSavedRecipes(sr.data || []);
        setChefRecipes(cr.data?.items || []);
      } catch {}
    })();
  }, [isAdmin]);

  const dirty = useMemo(() => JSON.stringify(project) !== JSON.stringify(EMPTY), [project]);

  // Honor ?base=<saved_recipe_id> from Discovery's "Send to R&D" action.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const base = searchParams.get("base");
    if (!base || !isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get(`/recipes/${base}`);
        // Pick up auto-suggested factors stashed by DiscoverPage
        let autoFactors = [];
        if (searchParams.get("auto_factors") === "1") {
          try {
            const raw = localStorage.getItem(`kostra:rnd:auto-factors:${base}`);
            if (raw) {
              autoFactors = JSON.parse(raw);
              localStorage.removeItem(`kostra:rnd:auto-factors:${base}`);
            }
          } catch {}
        }
        setProject((p) => ({
          ...p,
          name: `R&D: ${data.dish_name}`,
          base_recipe_id: base,
          base_recipe_snapshot: data,
          factors: autoFactors.length ? autoFactors : (p.factors || []),
        }));
        setTab("setup");
        if (autoFactors.length) {
          toast.success(`Linked "${data.dish_name}" + auto-added ${autoFactors.length} factors`);
        } else {
          toast.success(`Linked "${data.dish_name}" as base recipe`);
        }
        setSearchParams({}, { replace: true });
      } catch { toast.error("Could not auto-link base recipe"); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isAdmin]);

  const setField = (patch) => setProject((p) => ({ ...p, ...patch }));

  const newProject = () => {
    setProject(EMPTY);
    setTab("setup");
  };

  const loadProject = async (id) => {
    try {
      const { data } = await api.get(`/rnd/projects/${id}`);
      setProject({ ...EMPTY, ...data });
      setTab("setup");
    } catch { toast.error("Failed to load project"); }
  };

  const attachBaseRecipe = async (kind, refId) => {
    if (!refId) {
      setField({ base_recipe_id: null, base_recipe_snapshot: null });
      return;
    }
    try {
      const { data } = kind === "chef"
        ? await api.get(`/chef-recipes/${refId}`)
        : await api.get(`/recipes/${refId}`);
      setField({
        base_recipe_id: kind === "chef" ? `chef:${refId}` : refId,
        base_recipe_snapshot: data,
      });
      toast.success(`Linked "${data.dish_name}" as base recipe`);
    } catch { toast.error("Could not load recipe"); }
  };

  const persist = async () => {
    if (!isAdmin) return openLogin();
    setBusy(true);
    try {
      const payload = { ...project };
      delete payload.id;
      if (project.id) {
        const { data } = await api.put(`/rnd/projects/${project.id}`, payload);
        setProject({ ...EMPTY, ...data });
        toast.success("Project saved");
      } else {
        const { data } = await api.post(`/rnd/projects`, payload);
        setProject({ ...EMPTY, ...data });
        toast.success("Project created");
      }
      const refresh = await api.get("/rnd/projects");
      setProjects(refresh.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const removeProject = async () => {
    if (!project.id) return;
    if (!window.confirm(`Delete "${project.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/rnd/projects/${project.id}`);
      setProject(EMPTY);
      const refresh = await api.get("/rnd/projects");
      setProjects(refresh.data || []);
      toast.success("Project deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const generateDesign = async () => {
    if ((project.factors || []).length < 2) {
      toast.error("Add at least 2 factors first");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/rnd/design", {
        factors: project.factors,
        design_type: project.design_type,
        center_points: project.design_meta?.center_points ?? 3,
        alpha: project.design_meta?.alpha ?? "rotatable",
      });
      const trials = (data.runs || []).map((r) => ({
        trial_no: r.trial_no, levels: r.levels, results: {}, notes: "",
      }));
      setField({ trials });
      setTab("trials");
      toast.success(`${data.n} trials generated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  const downloadPdf = async () => {
    if (!project.id) { toast.error("Save the project first"); return; }
    try {
      const { data } = await api.get(`/rnd/projects/${project.id}/export/pdf`, { responseType: "blob" });
      const blob = new Blob([data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `KOSTRA_RnD_${(project.name || "project").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Report downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const downloadExcel = async () => {
    if (!project.id) { toast.error("Save the project first"); return; }
    try {
      const { data } = await api.get(`/rnd/projects/${project.id}/export/excel`, { responseType: "blob" });
      const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `KOSTRA_RnD_${(project.name || "project").replace(/\s+/g, "_")}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error("Excel export failed");
    }
  };

  const minRunsNeeded = useMemo(() => {
    const k = (project.factors || []).length;
    return 1 + 2 * k + (k * (k - 1)) / 2;
  }, [project.factors]);

  // ---- Guest view ----------------------------------------------------------
  if (!isAdmin) {
    return (
      <div className="app-shell">
        <Header />
        <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white">
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 text-center">
            <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-2">
              <FlaskConical size={14} /> R&amp;D · Product Optimization
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-medium mt-3 leading-tight">
              Run scientific experiments. <br className="hidden md:block" />
              <span className="text-[#D4AF37]">Find the perfect formulation.</span>
            </h1>
            <p className="text-white/75 text-sm md:text-base mt-3 max-w-2xl mx-auto">
              Design RSM experiments (Central Composite, Box-Behnken, Full Factorial), score each trial,
              fit quadratic models, and discover the optimum balance of taste, texture, yield and cost
              with weighted desirability optimization.
            </p>
            <Button
              onClick={() => openLogin()}
              className="mt-6 bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold h-11 px-6"
              data-testid="rnd-guest-login-btn"
            >
              Admin login to start an R&amp;D project
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ---- Admin view ----------------------------------------------------------
  return (
    <div className="app-shell">
      <Header />
      <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-8 pb-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-2">
                <FlaskConical size={14} /> R&amp;D · Product Optimization
              </div>
              <h1 className="font-display text-4xl md:text-5xl font-medium mt-2 leading-tight">
                Engineer the formula, <br className="hidden md:block" />
                <span className="text-[#D4AF37]">backed by data.</span>
              </h1>
              <p className="text-white/75 text-sm mt-3 max-w-2xl">
                Define factors, auto-generate an experimental design, score trials in your kitchen,
                fit a quadratic response surface, and find the optimum that balances every metric you care about.
              </p>
            </div>
            <div className="flex flex-col items-stretch md:items-end gap-3 shrink-0">
              <div className="flex flex-wrap gap-2 md:justify-end">
                <Button onClick={newProject} variant="outline"
                        className="bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white font-medium"
                        data-testid="rnd-new-btn">
                  <Plus size={14} className="mr-1.5" /> New Project
                </Button>
                <Button onClick={persist} disabled={busy}
                        className="bg-white text-[#0A3B24] hover:bg-[#FDF9EE] font-semibold border border-[#D4AF37]"
                        data-testid="rnd-save-btn">
                  <Save size={14} className="mr-1.5" /> {busy ? "Saving..." : (project.id ? "Update" : "Save")}
                </Button>
                <Button onClick={downloadPdf} disabled={!project.id}
                        className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold"
                        data-testid="rnd-pdf-btn">
                  <FileDown size={14} className="mr-1.5" /> R&amp;D Report PDF
                </Button>
                <Button onClick={downloadExcel} disabled={!project.id}
                        variant="outline"
                        className="bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white font-medium"
                        data-testid="rnd-excel-btn">
                  <FileDown size={14} className="mr-1.5" /> Excel
                </Button>
                {project.id && (
                  <Button onClick={removeProject} variant="outline"
                          className="bg-transparent text-white border-white/30 hover:bg-[#7F1D1D]/40 hover:text-white font-medium"
                          data-testid="rnd-delete-btn">
                    <Trash size={14} className="mr-1.5" /> Delete
                  </Button>
                )}
              </div>
              {projects.length > 0 && (
                <div className="flex items-center gap-2">
                  <Label className="kostra-data-label !text-white/70">Open project</Label>
                  <Select value={project.id || ""} onValueChange={loadProject}>
                    <SelectTrigger className="h-9 w-64 bg-white/10 border-white/20 text-white"
                                   data-testid="rnd-project-picker">
                      <SelectValue placeholder="Pick a saved project..." />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name} <span className="text-[10px] text-[#4B5563] ml-1">· {p.category}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-5 md:px-8 -mt-6 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-5"
        >
          <Tabs value={tab} onValueChange={setTab} className="space-y-5">
            <div className="kostra-card p-2 overflow-x-auto" data-testid="rnd-tabs">
              <TabsList className="bg-transparent p-0 gap-1.5 flex flex-nowrap md:flex-wrap w-full justify-start h-auto">
                <TabsTrigger value="setup" data-testid="rnd-tab-setup"
                             className="kostra-tab-trigger data-[state=active]:bg-[#0A3B24] data-[state=active]:text-white">
                  <ListChecks size={16} className="mr-2" /> Setup
                </TabsTrigger>
                <TabsTrigger value="trials" data-testid="rnd-tab-trials"
                             className="kostra-tab-trigger data-[state=active]:bg-[#0A3B24] data-[state=active]:text-white">
                  <FlaskConical size={16} className="mr-2" /> Trials
                </TabsTrigger>
                <TabsTrigger value="analysis" data-testid="rnd-tab-analysis"
                             className="kostra-tab-trigger data-[state=active]:bg-[#0A3B24] data-[state=active]:text-white">
                  <BarChart3 size={16} className="mr-2" /> Analysis
                </TabsTrigger>
                <TabsTrigger value="optimize" data-testid="rnd-tab-optimize"
                             className="kostra-tab-trigger data-[state=active]:bg-[#0A3B24] data-[state=active]:text-white">
                  <Target size={16} className="mr-2" /> Optimize
                </TabsTrigger>
                <TabsTrigger value="report" data-testid="rnd-tab-report"
                             className="kostra-tab-trigger data-[state=active]:bg-[#0A3B24] data-[state=active]:text-white">
                  <BookText size={16} className="mr-2" /> Report
                </TabsTrigger>
              </TabsList>
            </div>

            {/* SETUP */}
            <TabsContent value="setup" className="m-0 space-y-5">
              <div className="kostra-card p-5 grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-5">
                  <Label className="kostra-data-label mb-1 block">Product Name</Label>
                  <Input value={project.name}
                         onChange={(e) => setField({ name: e.target.value })}
                         className="h-11" data-testid="rnd-name-input" />
                </div>
                <div className="md:col-span-3">
                  <Label className="kostra-data-label mb-1 block">Category</Label>
                  <Select value={project.category} onValueChange={(v) => setField({ category: v })}>
                    <SelectTrigger className="h-11" data-testid="rnd-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-4">
                  <Label className="kostra-data-label mb-1 block">Objective</Label>
                  <Select value={project.objective} onValueChange={(v) => setField({ objective: v })}>
                    <SelectTrigger className="h-11" data-testid="rnd-objective"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {OBJECTIVES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="kostra-card p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="kostra-section-title text-lg">Base Recipe (optional)</h3>
                    <p className="text-xs text-[#4B5563] mt-1">
                      Link a recipe so factor changes flow into the costing engine in real time.
                    </p>
                  </div>
                  {project.base_recipe_snapshot && (
                    <span className="gold-pill text-[11px] font-semibold px-2.5 py-1 rounded-full">
                      Linked: {project.base_recipe_snapshot.dish_name}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="kostra-data-label mb-1 block">From your saved recipes</Label>
                    <Select onValueChange={(v) => attachBaseRecipe("saved", v)}>
                      <SelectTrigger className="h-10"
                                     data-testid="rnd-base-saved"><SelectValue placeholder="Pick a saved recipe..." /></SelectTrigger>
                      <SelectContent>
                        {savedRecipes.length === 0 ? (
                          <SelectItem value="__no_saved" disabled>No saved recipes yet</SelectItem>
                        ) : (
                          savedRecipes.map((r) => (
                            <SelectItem key={r.id} value={r.id}>{r.dish_name}</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="kostra-data-label mb-1 block">From the Inspiration library</Label>
                    <Select onValueChange={(v) => attachBaseRecipe("chef", v)}>
                      <SelectTrigger className="h-10"
                                     data-testid="rnd-base-chef"><SelectValue placeholder="Pick a chef recipe..." /></SelectTrigger>
                      <SelectContent>
                        {chefRecipes.slice(0, 60).map((r) => (
                          <SelectItem key={r.slug} value={r.slug}>{r.dish_name} <span className="text-[10px] text-[#4B5563]">· {r.section}</span></SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => attachBaseRecipe(null, null)}
                      className="w-full h-10 border-[#0A3B24]/30 text-[#0A3B24] hover:bg-[#FDF9EE] font-medium"
                      data-testid="rnd-base-none"
                    >
                      Not Required (Pure DOE)
                    </Button>
                  </div>
                </div>
                <p className="text-[11px] text-[#4B5563] -mt-1">
                  Pick "Not Required" to run a pure design of experiments — factors won't auto-cost,
                  but RSM, optimization and PDF export still work.
                </p>
                {project.base_recipe_snapshot && (
                  <Button type="button" variant="outline" onClick={() => attachBaseRecipe(null, null)}
                          className="border-[#0A3B24]/30 text-[#0A3B24] mt-2">
                    Unlink base recipe
                  </Button>
                )}
              </div>

              <div className="kostra-card p-5">
                <FactorEditor
                  factors={project.factors || []}
                  onChange={(factors) => setField({ factors })}
                  baseRecipe={project.base_recipe_snapshot}
                />
              </div>

              <div className="kostra-card p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                <div className="md:col-span-4">
                  <Label className="kostra-data-label mb-1 block">Design type</Label>
                  <Select value={project.design_type} onValueChange={(v) => setField({ design_type: v })}>
                    <SelectTrigger className="h-11" data-testid="rnd-design-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ccd">Central Composite (CCD)</SelectItem>
                      <SelectItem value="bbd">Box-Behnken (BBD)</SelectItem>
                      <SelectItem value="ff2">Full Factorial 2-level</SelectItem>
                      <SelectItem value="ff3">Full Factorial 3-level</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-3">
                  <Label className="kostra-data-label mb-1 block">Center points</Label>
                  <Input type="number" min="0" max="10"
                         value={project.design_meta?.center_points ?? 3}
                         onChange={(e) => setField({ design_meta: { ...project.design_meta, center_points: parseInt(e.target.value || 0, 10) } })}
                         className="h-11 font-mono-num" data-testid="rnd-center-points" />
                </div>
                <div className="md:col-span-3">
                  <Label className="kostra-data-label mb-1 block">CCD axial (alpha)</Label>
                  <Select value={String(project.design_meta?.alpha ?? "rotatable")}
                          onValueChange={(v) => setField({ design_meta: { ...project.design_meta, alpha: v } })}>
                    <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rotatable">Rotatable (default)</SelectItem>
                      <SelectItem value="face">Face-centered (α = 1)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Button onClick={generateDesign} disabled={generating || (project.factors || []).length < 2}
                          className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold w-full h-11"
                          data-testid="rnd-generate-design">
                    <Wand2 size={14} className="mr-1.5" /> {generating ? "Generating..." : "Generate"}
                  </Button>
                </div>
                <div className="md:col-span-12 text-xs text-[#4B5563]">
                  Quadratic model needs ≥ <strong>{minRunsNeeded}</strong> valid trials.
                </div>
              </div>
            </TabsContent>

            {/* TRIALS */}
            <TabsContent value="trials" className="m-0">
              <div className="kostra-card p-5">
                <TrialsTable
                  project={project}
                  onChange={(patch) => setField(patch)}
                />
              </div>
            </TabsContent>

            {/* ANALYSIS */}
            <TabsContent value="analysis" className="m-0">
              <div className="kostra-card p-5">
                <AnalysisPanel project={project} />
              </div>
            </TabsContent>

            {/* OPTIMIZE */}
            <TabsContent value="optimize" className="m-0">
              <div className="kostra-card p-5">
                <OptimizePanel project={project} />
              </div>
            </TabsContent>

            {/* REPORT / NOTES */}
            <TabsContent value="report" className="m-0 space-y-5">
              <div className="kostra-card p-5">
                <h3 className="kostra-section-title text-lg">R&amp;D Summary Notes</h3>
                <p className="text-xs text-[#4B5563] mt-1 mb-2">
                  Capture the storyline of the project — what you tried, what worked, what surprised you.
                </p>
                <Textarea
                  value={project.notes || ""}
                  onChange={(e) => setField({ notes: e.target.value })}
                  rows={6}
                  data-testid="rnd-notes"
                  placeholder="Hypothesis, conclusions, next experiments..."
                />
              </div>
              <div className="kostra-card p-5 flex items-center justify-between gap-4">
                <div>
                  <h3 className="kostra-section-title text-lg">Product Development Report</h3>
                  <p className="text-xs text-[#4B5563] mt-1">
                    Branded PDF with factors, trial table, regression results, optimum and your notes.
                  </p>
                </div>
                <Button onClick={downloadPdf} disabled={!project.id}
                        className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold"
                        data-testid="rnd-pdf-btn-2">
                  <FileDown size={14} className="mr-1.5" /> Download PDF
                </Button>
                <Button onClick={downloadExcel} disabled={!project.id} variant="outline"
                        className="border-[#0A3B24]/30 text-[#0A3B24] hover:bg-[#FDF9EE] font-medium ml-2"
                        data-testid="rnd-excel-btn-2">
                  <FileDown size={14} className="mr-1.5" /> Excel
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
